import { ReviewModal } from '@kw/quokka-ui';
import { useSnackbar } from 'notistack';
import { sendHttpRequest } from '../../../utils/network.service';

export function GuardianReviewModal({
  selectedItem,
  setLoading,
  fetchData,
  reviewType,
  afterSaveArgs = [],
  analysisId,
  isOpen,
  setIsOpen,
}: {
  selectedItem: any;
  setLoading?: (loading: boolean) => void;
  fetchData?: (...args: any[]) => Promise<void>;
  reviewType: 'rule' | 'regulation';
  afterSaveArgs?: any[];
  analysisId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const reviewTypeMapping = {
    rule: {
      id: selectedItem?.guid,
      name:
        selectedItem?.rule?.name ?? selectedItem?.rule?.shortDescription?.text ?? selectedItem?.rule?.fullDescription?.text?.split('.')[0],
      metadata: selectedItem?.metadata ?? [],
      label: 'Rule',
      ruleId: selectedItem?.rule?.id,
    },
    regulation: {
      id: selectedItem?.regulationControlId,
      name: selectedItem?.regulationControlId,
      metadata: selectedItem?.regulationControlMetadata ?? [],
      label: 'Regulation',
      ruleId: selectedItem?.regulationControlId,
    },
  };

  const stop = e => e?.stopPropagation?.();

  const toggleSingle = (e?: React.MouseEvent) => {
    stop(e);
    setIsOpen(!isOpen);
  };

  // Build review data from adapters
  const { id, name, metadata, label, ruleId } = reviewTypeMapping[reviewType];

  const future = metadata.find(m => m?.appliesToFutureVersions);
  const current = metadata.find(m => !m?.appliesToFutureVersions);

  const futureReviewData = {
    ...future,
    id,
    name,
  };

  const currentReviewData = {
    ...current,
    id,
    name,
    ruleId,
  };

  const handleSaveReview = async (params: {
    id: string;
    ruleId: string;
    singleVersionNote: string;
    singleVersionReviewStatus: string;
    futureVersionReviewStatus: string;
    futureVersionsNote: string;
  }) => {
    setLoading?.(true);
    try {
      // Helper to determine if review data has changed
      const hasChanged = (paramsStatus: string, paramsNote: string, reviewData: any) => {
        return paramsStatus !== (reviewData.reviewStatus || 'default') || paramsNote !== (reviewData.note || '');
      };

      const currentChanged = hasChanged(params.singleVersionReviewStatus, params.singleVersionNote, currentReviewData);
      const futureChanged = hasChanged(params.futureVersionReviewStatus, params.futureVersionsNote, futureReviewData);

      // Prepare API call for current version if changed
      if (currentChanged) {
        await sendHttpRequest({
          path: `${process.env.EVAL_SERVICE_URL}/eval/review-metadata/${analysisId}`,
          method: 'POST',
          body: {
            reviewType,
            ruleId: params.ruleId,
            reviewId: params.id,
            reviewStatus: params.singleVersionReviewStatus,
            note: params.singleVersionNote,
            appliesToFutureVersions: false,
          },
          successMessage: 'Successfully updated review for current version',
          errorMessage: 'Error updating review',
          enqueueSnackbar,
        });
      }

      // Prepare API call for future version if changed
      if (futureChanged) {
        await sendHttpRequest({
          path: `${process.env.EVAL_SERVICE_URL}/eval/review-metadata/${analysisId}`,
          method: 'POST',
          body: {
            reviewType,
            ruleId: params.ruleId,
            reviewId: params.id,
            reviewStatus: params.futureVersionReviewStatus,
            note: params.futureVersionsNote,
            appliesToFutureVersions: true,
          },
          successMessage: 'Successfully updated review for future versions',
          errorMessage: 'Error updating review',
          enqueueSnackbar,
        });
      }
    } finally {
      await fetchData?.(...afterSaveArgs);
      setLoading?.(false);
    }
  };

  return (
    isOpen && (
      <ReviewModal
        key={currentReviewData.id}
        isOpen={isOpen}
        toggle={toggleSingle}
        reviewItem={label}
        saveReviewCallback={handleSaveReview}
        selectedVersionReviewData={currentReviewData}
        futureVersionsReviewData={futureReviewData}
      />
    )
  );
}
